/**
 * Always-on presence connection — ONE per page, independent of React routes.
 *
 * Contract with the backend (packages/core/src/modules/socket/presenceSocketHandler.js):
 *  - identity = the JWT in the handshake `auth.token`, minted by POST /user/socket-token (5 min) and
 *    fetched again on EVERY (re)connect through socket.io-client's `auth` function form;
 *  - `presence:heartbeat { visible }` every `heartbeatMs` (server ack tells us the interval);
 *  - engine.io pongs on this same connection also count server-side, so a backgrounded tab stays online;
 *  - `presence:changed { walletAddress, online, at }` arrives for self, friends and alliance members.
 *
 * Never torn down on navigation. Stopped only when the wallet address changes/clears or on real page
 * unload (`pagehide` without bfcache).
 */
import axios from 'axios'
import { io } from 'socket.io-client'
import type { Socket } from 'socket.io-client'

import {
  presenceChanged,
  presenceSelfAck,
  presenceSelfConnection,
  presenceReset,
} from '../common/state/presence/reducer'
import store from '../store'
// Importing the bootstrap also applies the shared axios defaults (baseURL / withCredentials).
import { recoverSessionAfter401 } from '../utils/axiosSessionBootstrap'
import config from '../utils/config'

const DEFAULT_HEARTBEAT_MS = 30_000
/** Re-check the manager this often; a manager that stopped retrying gets kicked with `connect()`. */
const WATCHDOG_MS = 15_000
/** Refresh the cached socket token this long before its stated expiry. */
const TOKEN_REFRESH_MARGIN_MS = 30_000

let socket: Socket | null = null
let wallet: string | null = null
let heartbeatMs = DEFAULT_HEARTBEAT_MS
let beatTimer: ReturnType<typeof setInterval> | null = null
let watchdogTimer: ReturnType<typeof setInterval> | null = null
let cachedToken: { token: string; expiresAt: number } | null = null
let tokenInFlight: Promise<string | null> | null = null
let listenersBound = false

function normWallet(w: string | null | undefined): string {
  return String(w || '')
    .toLowerCase()
    .trim()
}

/** Fetch (or reuse) the short-lived socket token. Returns null when the session cannot be authenticated. */
async function getSocketToken(force = false): Promise<string | null> {
  const now = Date.now()
  if (!force && cachedToken && cachedToken.expiresAt - TOKEN_REFRESH_MARGIN_MS > now) {
    return cachedToken.token
  }
  if (tokenInFlight) return tokenInFlight
  tokenInFlight = (async () => {
    const attempt = async (): Promise<string | null> => {
      const res = await axios.post('/user/socket-token', {})
      const d = res?.data
      if (d?.success && typeof d.token === 'string' && d.token) {
        const ttlMs = (Number(d.expiresIn) || 300) * 1000
        cachedToken = { token: d.token, expiresAt: Date.now() + ttlMs }
        return d.token
      }
      return null
    }
    try {
      return await attempt()
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } } | null)?.response?.status
      if (status === 401) {
        // Access token expired between beats: run the shared refresh once, then retry.
        const recovered = await recoverSessionAfter401().catch(() => false)
        if (recovered) {
          try {
            return await attempt()
          } catch {
            return null
          }
        }
      }
      return null
    } finally {
      tokenInFlight = null
    }
  })()
  return tokenInFlight
}

function isDocumentVisible(): boolean {
  return typeof document === 'undefined' ? true : document.visibilityState !== 'hidden'
}

function sendBeat(): void {
  const s = socket
  if (!s || !s.connected) return
  s.emit(
    'presence:heartbeat',
    { visible: isDocumentVisible() },
    (ack: { ok?: boolean; reason?: string; heartbeatMs?: number } | undefined) => {
      if (!ack) return
      store.dispatch(presenceSelfAck({ ok: !!ack.ok, reason: ack.reason ?? null }))
      const serverMs = Number(ack.heartbeatMs)
      if (Number.isFinite(serverMs) && serverMs >= 5_000 && serverMs !== heartbeatMs) {
        heartbeatMs = serverMs
        restartBeatTimer()
      }
      if (!ack.ok && ack.reason === 'unauthenticated') {
        // The server did not see a valid JWT on this connection: get a fresh token and reconnect.
        cachedToken = null
        void getSocketToken(true).then((t) => {
          if (t && socket) {
            socket.disconnect()
            socket.connect()
          }
        })
      }
    },
  )
}

function restartBeatTimer(): void {
  if (beatTimer) clearInterval(beatTimer)
  beatTimer = setInterval(sendBeat, heartbeatMs)
}

function kickIfStalled(): void {
  const s = socket
  if (!s) return
  // `active` is false once the manager gave up (or after an explicit disconnect); `connect()` restarts it.
  if (!s.connected && !s.active) {
    s.connect()
  }
}

function onVisibilityChange(): void {
  if (isDocumentVisible()) {
    kickIfStalled()
    sendBeat()
  } else {
    // Tell the server we are backgrounded (kept online, marked not visible).
    sendBeat()
  }
}

function onBrowserOnline(): void {
  kickIfStalled()
  sendBeat()
}

function onPageHide(e: PageTransitionEvent): void {
  if (e.persisted) return
  socket?.disconnect()
}

function bindWindowListeners(): void {
  if (listenersBound || typeof window === 'undefined') return
  listenersBound = true
  document.addEventListener('visibilitychange', onVisibilityChange)
  window.addEventListener('online', onBrowserOnline)
  window.addEventListener('focus', onBrowserOnline)
  window.addEventListener('pagehide', onPageHide)
}

function unbindWindowListeners(): void {
  if (!listenersBound || typeof window === 'undefined') return
  listenersBound = false
  document.removeEventListener('visibilitychange', onVisibilityChange)
  window.removeEventListener('online', onBrowserOnline)
  window.removeEventListener('focus', onBrowserOnline)
  window.removeEventListener('pagehide', onPageHide)
}

/**
 * Start (or keep) the presence connection for this wallet. Idempotent per wallet; a different wallet
 * tears the previous connection down first.
 */
export function startPresence(walletAddress: string | null | undefined): void {
  const w = normWallet(walletAddress)
  if (!w) {
    stopPresence()
    return
  }
  if (socket && wallet === w) {
    kickIfStalled()
    return
  }
  if (socket) stopPresence()

  wallet = w
  const apiKey = process.env.REACT_APP_X_API_KEY || ''
  const s = io(config.socketURL as string, {
    path: '/socket.io',
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
    randomizationFactor: 0.5,
    timeout: 20000,
    // Function form: evaluated on EVERY connect/reconnect so an expired socket token is replaced.
    auth: (cb) => {
      void getSocketToken().then((token) => {
        cb({
          ...(token ? { token } : {}),
          ...(apiKey ? { 'x-api-key': apiKey } : {}),
          presence: true,
        })
      })
    },
    extraHeaders: apiKey ? { 'x-api-key': apiKey } : {},
  })
  socket = s

  s.on('connect', () => {
    store.dispatch(presenceSelfConnection(true))
    sendBeat()
    restartBeatTimer()
  })
  s.on('disconnect', () => {
    store.dispatch(presenceSelfConnection(false))
  })
  s.on('connect_error', () => {
    // Network / handshake failure: nothing to do here, the manager keeps retrying (Infinity). If the
    // token fetch itself failed we simply reconnect without one; the server's ack will say so and we retry.
  })
  s.on('presence:changed', (payload: { walletAddress?: string; online?: boolean; at?: number }) => {
    if (!payload?.walletAddress) return
    store.dispatch(
      presenceChanged({
        walletAddress: payload.walletAddress,
        online: !!payload.online,
        at: payload.at,
      }),
    )
  })

  bindWindowListeners()
  if (watchdogTimer) clearInterval(watchdogTimer)
  watchdogTimer = setInterval(kickIfStalled, WATCHDOG_MS)
}

/** Tear everything down (wallet cleared / changed). */
export function stopPresence(): void {
  if (beatTimer) {
    clearInterval(beatTimer)
    beatTimer = null
  }
  if (watchdogTimer) {
    clearInterval(watchdogTimer)
    watchdogTimer = null
  }
  unbindWindowListeners()
  if (socket) {
    socket.removeAllListeners()
    socket.disconnect()
    socket = null
  }
  wallet = null
  cachedToken = null
  store.dispatch(presenceReset())
}

export function getPresenceSocket(): Socket | null {
  return socket
}

export function getPresenceWallet(): string | null {
  return wallet
}
